import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, Prescription } from '../../types';
import { Pill, CheckCircle, XCircle, Printer, Check, X, FileText } from 'lucide-react';
export const PharmacyDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser
  } = useData();
  // Use ID pattern for live updates
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(null);
  const selectedEncounter = selectedEncounterId ? encounters.find(e => e.id === selectedEncounterId) || null : null;
  // Local state to track dispensing before saving
  const [dispenseStatus, setDispenseStatus] = useState<Record<string, 'Dispensed' | 'Not Dispensed'>>({});
  // State for showing the prescription preview modal
  const [showPrescriptionPreview, setShowPrescriptionPreview] = useState(false);
  const pendingEncounters = encounters.filter(e => e.stage === 'PHARMACY');
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectEncounter = (encounter: Encounter) => {
    setSelectedEncounterId(encounter.id);
    const initialStatus: Record<string, 'Dispensed' | 'Not Dispensed'> = {};
    encounter.prescriptions.forEach(rx => {
      if (rx.status !== 'Pending') {
        initialStatus[rx.id] = rx.status;
      }
    });
    setDispenseStatus(initialStatus);
    setShowPrescriptionPreview(false);
  };
  const handleToggleStatus = (rxId: string, status: 'Dispensed' | 'Not Dispensed') => {
    setDispenseStatus(prev => ({
      ...prev,
      [rxId]: status
    }));
  };
  const handleCompleteDispensing = () => {
    if (!selectedEncounter || !currentUser) return;
    // Check if all drugs have a status
    const allProcessed = selectedEncounter.prescriptions.every(rx => dispenseStatus[rx.id]);
    if (!allProcessed) {
      alert('Please mark all drugs as Dispensed or Not Available.');
      return;
    }
    const updatedPrescriptions = selectedEncounter.prescriptions.map(rx => ({
      ...rx,
      status: dispenseStatus[rx.id],
      dispensedAt: new Date().toISOString(),
      dispensedBy: currentUser.id,
      pharmacistName: currentUser.fullName
    }));
    const hasUndispensed = updatedPrescriptions.some(rx => rx.status === 'Not Dispensed');
    updateEncounter(selectedEncounter.id, {
      prescriptions: updatedPrescriptions,
      stage: 'CLINICIAN'
    });
    alert(hasUndispensed ? 'Dispensing completed. Report sent back to Clinician for review (some drugs were not available).' : 'Dispensing completed. Report sent back to Clinician.');
    setSelectedEncounterId(null);
    setDispenseStatus({});
    setShowPrescriptionPreview(false);
  };
  const handlePrint = () => {
    window.print();
  };
  if (selectedEncounter) {
    const patient = getPatient(selectedEncounter.patientId);
    if (!patient) return <div data-id="element-1117">Patient not found</div>;
    const notDispensedDrugs = selectedEncounter.prescriptions.filter(rx => dispenseStatus[rx.id] === 'Not Dispensed');
    const allProcessed = selectedEncounter.prescriptions.every(rx => dispenseStatus[rx.id]);
    // If showing the prescription preview
    if (showPrescriptionPreview) {
      return <div className="space-y-6 pb-20" data-id="element-1118">
          <div className="flex justify-between items-center print:hidden sticky top-0 bg-gray-50 z-10 py-4 border-b border-gray-200" data-id="element-1119">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1120">
              Prescription Preview
            </h1>
            <div className="flex space-x-2" data-id="element-1121">
              <Button variant="outline" onClick={() => setShowPrescriptionPreview(false)} data-id="element-1122">
                Back to Dispensing
              </Button>
              <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700" data-id="element-1123">
                <Printer className="w-4 h-4 mr-2" data-id="element-1124" /> Print Prescription
              </Button>
            </div>
          </div>

          {/* A4 Prescription Form Container */}
          <div className="flex justify-center print:block print:m-0" data-id="element-1125">
            <div className="bg-white shadow-lg print:shadow-none w-full max-w-[210mm] min-h-[297mm] p-10 print:p-0 print:w-full print:max-w-none border border-gray-200 print:border-none" data-id="element-1126">
              {/* Form Header */}
              <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-6" data-id="element-1127">
                <div className="flex items-center" data-id="element-1128">
                  <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-24 w-24 object-contain mr-4" data-id="element-1129" />
                  <div data-id="element-1130">
                    <h1 className="text-3xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-1131">
                      BMED CARE
                    </h1>
                    <p className="text-sm italic text-gray-600" data-id="element-1132">
                      Maternity Care Made Better
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-1133">
                  <p className="font-bold text-xl uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-1134">
                    PRESCRIPTION FORM
                  </p>
                  <p className="text-sm" data-id="element-1135">
                    <span className="font-semibold" data-id="element-1136">Date:</span>{' '}
                    {new Date().toLocaleDateString()}
                  </p>
                  <p className="text-sm" data-id="element-1137">
                    <span className="font-semibold" data-id="element-1138">File No:</span>{' '}
                    {patient.bmcFileNumber}
                  </p>
                </div>
              </div>

              {/* Patient Details */}
              <div className="border-2 border-black p-4 mb-8 text-sm space-y-3" data-id="element-1139">
                <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1140">
                  <div className="col-span-8" data-id="element-1141">
                    <span className="font-semibold" data-id="element-1142">Patient Name:</span>{' '}
                    {patient.firstName} {patient.lastName}
                  </div>
                  <div className="col-span-2" data-id="element-1143">
                    <span className="font-semibold" data-id="element-1144">Age:</span> {patient.age}
                  </div>
                  <div className="col-span-2" data-id="element-1145">
                    <span className="font-semibold" data-id="element-1146">Sex:</span> {patient.sex}
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1147">
                  <div className="col-span-12" data-id="element-1148">
                    <span className="font-semibold" data-id="element-1149">Allergies:</span>{' '}
                    <span className="text-red-600 font-bold" data-id="element-1150">
                      {patient.allergies}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2" data-id="element-1151">
                  <div className="col-span-12" data-id="element-1152">
                    <span className="font-semibold" data-id="element-1153">
                      Prescribing Clinician:
                    </span>{' '}
                    {selectedEncounter.lastAttendingClinician}
                  </div>
                </div>
              </div>

              {/* Drugs Table */}
              <div className="mb-12" data-id="element-1154">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-1155">
                  Medication to be Sourced Externally
                </h3>
                <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-1156">
                  <thead data-id="element-1157">
                    <tr className="bg-gray-100" data-id="element-1158">
                      <th className="border border-black p-2 w-12" data-id="element-1159">No.</th>
                      <th className="border border-black p-2 text-left" data-id="element-1160">
                        Drug Name
                      </th>
                      <th className="border border-black p-2" data-id="element-1161">Dose</th>
                      <th className="border border-black p-2" data-id="element-1162">Route</th>
                      <th className="border border-black p-2" data-id="element-1163">Frequency</th>
                      <th className="border border-black p-2" data-id="element-1164">Days</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1165">
                    {notDispensedDrugs.map((rx, i) => <tr key={rx.id} data-id="element-1166">
                        <td className="border border-black p-3 font-medium" data-id="element-1167">
                          {i + 1}
                        </td>
                        <td className="border border-black p-3 text-left font-bold" data-id="element-1168">
                          {rx.drugName}
                        </td>
                        <td className="border border-black p-3" data-id="element-1169">{rx.dose}</td>
                        <td className="border border-black p-3" data-id="element-1170">{rx.route}</td>
                        <td className="border border-black p-3" data-id="element-1171">
                          {rx.frequency}
                        </td>
                        <td className="border border-black p-3" data-id="element-1172">{rx.days}</td>
                      </tr>)}
                    {/* Empty rows for padding if few drugs */}
                    {Array.from({
                    length: Math.max(0, 5 - notDispensedDrugs.length)
                  }).map((_, i) => <tr key={`empty-${i}`} data-id="element-1173">
                        <td className="border border-black p-4" data-id="element-1174"></td>
                        <td className="border border-black p-4" data-id="element-1175"></td>
                        <td className="border border-black p-4" data-id="element-1176"></td>
                        <td className="border border-black p-4" data-id="element-1177"></td>
                        <td className="border border-black p-4" data-id="element-1178"></td>
                        <td className="border border-black p-4" data-id="element-1179"></td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              {/* Footer Signatures */}
              <div className="mt-auto pt-10" data-id="element-1180">
                <div className="flex justify-between items-end" data-id="element-1181">
                  <div className="w-64" data-id="element-1182">
                    <p className="text-sm font-semibold mb-8" data-id="element-1183">
                      Pharmacist Signature:
                    </p>
                    <div className="border-b border-black" data-id="element-1184"></div>
                    <p className="text-sm mt-2 font-medium" data-id="element-1185">
                      {currentUser?.fullName}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1186">
                      BMED Pharmacy Dept
                    </p>
                  </div>
                  <div className="w-64" data-id="element-1187">
                    <p className="text-sm font-semibold mb-8" data-id="element-1188">
                      Official Stamp:
                    </p>
                    <div className="border border-black h-24 w-24 rounded-full mx-auto opacity-20" data-id="element-1189"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>;
    }
    // Standard Dispensing View
    return <div className="space-y-6 pb-20 print:hidden" data-id="element-1190">
        <div className="flex justify-between items-center sticky top-0 bg-gray-50 z-10 py-4 border-b border-gray-200" data-id="element-1191">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1192">
            Dispense Medication
          </h1>
          <div className="flex space-x-2" data-id="element-1193">
            <Button variant="outline" onClick={() => setSelectedEncounterId(null)} data-id="element-1194">
              Back to Queue
            </Button>
            {notDispensedDrugs.length > 0 && <Button variant="outline" onClick={() => setShowPrescriptionPreview(true)} className="border-blue-600 text-blue-600 hover:bg-blue-50" data-id="element-1195">
                <FileText className="w-4 h-4 mr-2" data-id="element-1196" /> Generate Prescription
              </Button>}
            <Button onClick={handleCompleteDispensing} disabled={!allProcessed} className={`${allProcessed ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-400 cursor-not-allowed'} text-white`} data-id="element-1197">
              <Check className="w-5 h-5 mr-2" data-id="element-1198" /> Complete Dispensing
            </Button>
          </div>
        </div>

        {/* Medical Form Style Container */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6" data-id="element-1199">
          {/* Patient Header */}
          <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-1200">
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1201">
              <div className="col-span-8" data-id="element-1202">
                <span className="font-semibold" data-id="element-1203">Patient Name:</span>{' '}
                {patient.firstName} {patient.lastName}
              </div>
              <div className="col-span-2" data-id="element-1204">
                <span className="font-semibold" data-id="element-1205">Age:</span> {patient.age}
              </div>
              <div className="col-span-2" data-id="element-1206">
                <span className="font-semibold" data-id="element-1207">Sex:</span> {patient.sex}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1208">
              <div className="col-span-6" data-id="element-1209">
                <span className="font-semibold" data-id="element-1210">File No:</span>{' '}
                {patient.bmcFileNumber}
              </div>
              <div className="col-span-6" data-id="element-1211">
                <span className="font-semibold" data-id="element-1212">Prescribing Clinician:</span>{' '}
                {selectedEncounter.lastAttendingClinician}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2" data-id="element-1213">
              <div className="col-span-12" data-id="element-1214">
                <span className="font-semibold" data-id="element-1215">Allergies:</span>{' '}
                <span className="text-red-600 font-bold" data-id="element-1216">
                  {patient.allergies}
                </span>
              </div>
            </div>
          </div>

          {/* DRUG TREATMENT / PRESCRIPTION */}
          <div className="border-2 border-black" data-id="element-1217">
            <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-1218">
              Drug Treatment / Prescription
            </h3>
            <table className="w-full border-collapse text-sm text-center" data-id="element-1219">
              <thead data-id="element-1220">
                <tr className="bg-gray-100" data-id="element-1221">
                  <th className="border border-black p-2 w-10" data-id="element-1222">No.</th>
                  <th className="border border-black p-2 text-left" data-id="element-1223">
                    Drug Name
                  </th>
                  <th className="border border-black p-2" data-id="element-1224">Dose</th>
                  <th className="border border-black p-2" data-id="element-1225">Route</th>
                  <th className="border border-black p-2" data-id="element-1226">Frequency</th>
                  <th className="border border-black p-2" data-id="element-1227">Days</th>
                  <th className="border border-black p-2 w-64" data-id="element-1228">
                    Dispense Status
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1229">
                {selectedEncounter.prescriptions.map((rx, i) => <tr key={rx.id} className={dispenseStatus[rx.id] === 'Not Dispensed' ? 'bg-red-50' : dispenseStatus[rx.id] === 'Dispensed' ? 'bg-green-50' : ''} data-id="element-1230">
                    <td className="border border-black p-2 font-medium" data-id="element-1231">
                      {i + 1}
                    </td>
                    <td className="border border-black p-2 text-left font-bold" data-id="element-1232">
                      {rx.drugName}
                    </td>
                    <td className="border border-black p-2" data-id="element-1233">{rx.dose}</td>
                    <td className="border border-black p-2" data-id="element-1234">{rx.route}</td>
                    <td className="border border-black p-2" data-id="element-1235">{rx.frequency}</td>
                    <td className="border border-black p-2" data-id="element-1236">{rx.days}</td>
                    <td className="border border-black p-2" data-id="element-1237">
                      <div className="flex space-x-1 justify-center" data-id="element-1238">
                        <button className={`px-3 py-1.5 rounded flex items-center text-xs font-bold transition-colors ${dispenseStatus[rx.id] === 'Dispensed' ? 'bg-green-600 text-white shadow-inner' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'}`} onClick={() => handleToggleStatus(rx.id, 'Dispensed')} data-id="element-1239">
                          <CheckCircle className="w-3 h-3 mr-1" data-id="element-1240" /> Dispensed
                        </button>
                        <button className={`px-3 py-1.5 rounded flex items-center text-xs font-bold transition-colors ${dispenseStatus[rx.id] === 'Not Dispensed' ? 'bg-red-600 text-white shadow-inner' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'}`} onClick={() => handleToggleStatus(rx.id, 'Not Dispensed')} data-id="element-1241">
                          <XCircle className="w-3 h-3 mr-1" data-id="element-1242" /> Not Avail
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* NAME OF MEDICAL PRACTITIONER */}
          <div className="border-2 border-black p-4 mt-6" data-id="element-1243">
            <p className="font-bold text-sm" data-id="element-1244">
              Dispensing Pharmacist:{' '}
              <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-1245">
                {currentUser?.fullName || ''}
              </span>
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6 print:hidden" data-id="element-1246">
      <div className="flex justify-between items-center" data-id="element-1247">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1248">Pharmacy Dashboard</h1>
        <Badge variant="success" className="text-sm px-3 py-1 bg-teal-100 text-teal-800" data-id="element-1249">
          {pendingEncounters.length} Prescriptions Waiting
        </Badge>
      </div>

      <Card noPadding data-id="element-1250">
        <ul className="divide-y divide-gray-200" data-id="element-1251">
          {pendingEncounters.length > 0 ? pendingEncounters.map(encounter => {
          const patient = getPatient(encounter.patientId);
          if (!patient) return null;
          return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-1252">
                  <div className="flex items-center" data-id="element-1253">
                    <div className="bg-teal-100 p-2 rounded-full mr-4" data-id="element-1254">
                      <Pill className="h-6 w-6 text-teal-600" data-id="element-1255" />
                    </div>
                    <div data-id="element-1256">
                      <p className="text-sm font-medium text-blue-600" data-id="element-1257">
                        {patient.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1258">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-1259">
                        {encounter.prescriptions.length} items prescribed by{' '}
                        {encounter.lastAttendingClinician}
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectEncounter(encounter)} className="bg-teal-600 hover:bg-teal-700 text-white" data-id="element-1260">
                    View Prescription
                  </Button>
                </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-1261">
              <Pill className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-1262" />
              <p className="text-lg font-medium text-gray-900" data-id="element-1263">
                No pending prescriptions
              </p>
            </li>}
        </ul>
      </Card>
    </div>;
};